'use client';

import { useEffect } from 'react';
import logger from '@/lib/logger';

function errorDetails(value: unknown): Record<string, unknown> {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  if (value && typeof value === 'object') {
    const maybeResponse = (value as { response?: { status?: unknown; statusText?: unknown } }).response;
    return {
      name: (value as { name?: unknown }).name,
      message: (value as { message?: unknown }).message,
      stack: (value as { stack?: unknown }).stack,
      status: maybeResponse?.status,
      statusText: maybeResponse?.statusText,
    };
  }
  return { message: typeof value === 'string' ? value : 'Unknown error' };
}

export function GlobalErrorHandler() {
  useEffect(() => {
    // Flag to prevent infinite recursion
    let isLogging = false;

    // Capture unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isLogging) return;
      isLogging = true;
      try {
        const details = errorDetails(event.reason);
        logger.error('UnhandledPromiseRejection', String(details.message || 'Promise rejected'), details);
      } finally {
        isLogging = false;
      }
    };

    // Capture global errors
    const handleError = (event: ErrorEvent) => {
      if (isLogging) return;
      isLogging = true;
      try {
        const details = errorDetails(event.error);
        logger.error('GlobalError', event.message, {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          ...details,
        });
      } finally {
        isLogging = false;
      }
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return null;
}
